import { getLocalizationSnapshot } from "@/lib/localization";

const RAW_API_BASE_URL = (
  process.env.REACT_APP_SHOP_API_URL ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "http://127.0.0.1:8080"
).trim();

function isLoopbackHostname(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function resolveApiBaseUrl(baseUrl) {
  if (typeof window === "undefined") {
    return baseUrl;
  }

  try {
    const currentHost = window.location.hostname;
    const apiUrl = new URL(baseUrl);

    if (isLoopbackHostname(currentHost) && isLoopbackHostname(apiUrl.hostname) && apiUrl.hostname !== currentHost) {
      apiUrl.hostname = currentHost;
      return apiUrl.toString().replace(/\/$/, "");
    }
  } catch {
    return baseUrl;
  }

  return baseUrl;
}

export const API_BASE_URL = resolveApiBaseUrl(RAW_API_BASE_URL);

const DEVICE_ID_KEY = "shopbook.web.device-id";
const SESSION_HEADER = (process.env.REACT_APP_SHOP_SESSION_HEADER || "X-Shopflow-Session").trim();
const REQUEST_TIMEOUT_MS = 20_000;

let csrfToken = null;
let sessionInvalidHandler = null;
let subscriptionReadOnlyHandler = null;
let insufficientCreditsHandler = null;

async function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  options.signal?.addEventListener("abort", abortFromCaller, { once: true });
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs || REQUEST_TIMEOUT_MS);
  const { timeoutMs: _timeoutMs, ...fetchOptions } = options;
  try {
    return await fetch(url, { ...fetchOptions, signal: controller.signal });
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      getRequestCopy().networkUnavailable || "We couldn't connect. Check that Wi-Fi or mobile data is working, then try again.",
      0,
      controller.signal.aborted ? "NETWORK_TIMEOUT" : "NETWORK_UNAVAILABLE",
    );
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener("abort", abortFromCaller);
  }
}

export class ApiError extends Error {
  constructor(message, status, code = "") {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

function getRequestCopy() {
  return getLocalizationSnapshot().messages.settings;
}

function getStoredValue(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setStoredValue(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore persistence failures in private/incognito contexts.
  }
}

function removeStoredValue(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore persistence failures in private/incognito contexts.
  }
}

async function getDeviceId() {
  const cached = getStoredValue(DEVICE_ID_KEY);
  if (cached) {
    return cached;
  }

  const next =
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  setStoredValue(DEVICE_ID_KEY, next);
  return next;
}

async function ensureCsrfToken() {
  if (csrfToken) {
    return csrfToken;
  }

  const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/csrf`, {
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });

  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || getRequestCopy().requestPrepareFailed);
  }

  const payload = text ? safeParseJson(text) : {};
  const responseHeaderToken = response.headers.get("X-CSRF-Token");
  const nextToken = payload.csrfToken || responseHeaderToken;

  if (!nextToken) {
    throw new Error(getRequestCopy().csrfTokenUnavailable);
  }

  csrfToken = nextToken;
  return csrfToken;
}

export async function request(path, options = {}) {
  return requestWithParser(path, options, "json");
}

export async function requestText(path, options = {}) {
  return requestWithParser(path, options, "text");
}

export async function streamSSE(path, { lastEventId = 0, onEvent, sessionId, signal } = {}) {
  const headers = new Headers({ Accept: "text/event-stream" });
  headers.set("X-Shopflow-Device", "Shopbook Web");
  headers.set("X-Shopflow-Device-ID", await getDeviceId());
  headers.set("X-Shopflow-Inventory-Capability", "device-grants-v1");
  headers.set("X-Shopflow-Platform", "web");
  headers.set("X-Shopflow-Language", getLocalizationSnapshot().language);
  headers.set("Content-Language", "und");
  if (sessionId) {
    headers.set(SESSION_HEADER, sessionId);
  }

  const separator = path.includes("?") ? "&" : "?";
  const streamPath = `${path}${separator}after=${Math.max(0, Number(lastEventId) || 0)}`;
  const response = await fetch(`${API_BASE_URL}${streamPath}`, {
    credentials: "include",
    headers,
    method: "GET",
    signal,
  });
  if (!response.ok || !response.body) {
    const text = await response.text();
    const payload = text ? safeParseJson(text) : {};
    const message = payload.error || text || getRequestCopy().requestFailedGeneric;
    if (isInvalidSessionStatusOrMessage(response.status, message)) {
      sessionInvalidHandler?.({ code: payload.code || "AUTHENTICATION_REQUIRED", message });
    }
    throw new ApiError(message, response.status, payload.code || "");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const parsed = parseSSEBlock(block);
      if (parsed) {
        await onEvent?.(parsed);
      }
      boundary = buffer.indexOf("\n\n");
    }
  }
}

export function parseSSEBlock(block) {
  if (!block || block.startsWith(":")) return null;
  let event = "message";
  let id = 0;
  const data = [];
  block.split("\n").forEach((line) => {
    const separator = line.indexOf(":");
    const field = separator >= 0 ? line.slice(0, separator) : line;
    const value = separator >= 0 ? line.slice(separator + 1).replace(/^ /, "") : "";
    if (field === "event") event = value;
    if (field === "id") id = Number(value) || 0;
    if (field === "data") data.push(value);
  });
  if (data.length === 0) return null;
  const rawData = data.join("\n");
  return { data: safeParseJson(rawData), event, id };
}

async function requestWithParser(path, options = {}, responseType = "json") {
  const method = options.method || "GET";
  const isUnsafeRequest = method !== "GET" && method !== "HEAD";
  const headers = new Headers(options.headers || {});

  if (!headers.has("Accept")) {
    headers.set("Accept", responseType === "text" ? "text/csv,text/plain,*/*" : "application/json");
  }
  headers.set("X-Shopflow-Device", "Shopbook Web");
  headers.set("X-Shopflow-Device-ID", await getDeviceId());
  headers.set("X-Shopflow-Inventory-Capability", "device-grants-v1");
  headers.set("X-Shopflow-Platform", "web");
  headers.set("X-Shopflow-Language", getLocalizationSnapshot().language);
  if (!headers.has("Content-Language")) {
    headers.set("Content-Language", "und");
  }

  if (options.sessionId) {
    headers.set(SESSION_HEADER, options.sessionId);
  }

  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  if (options.body && !isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (isUnsafeRequest) {
    headers.set("X-CSRF-Token", await ensureCsrfToken());
  }

  let response = await fetchWithTimeout(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: "include",
    headers,
    method,
  });

  let responseText = await response.text();
  let payload = responseText ? safeParseJson(responseText) : {};

  if (!response.ok && isUnsafeRequest && isCsrfFailure(response.status, payload, responseText)) {
    csrfToken = null;
    headers.set("X-CSRF-Token", await ensureCsrfToken());

    response = await fetchWithTimeout(`${API_BASE_URL}${path}`, {
      ...options,
      credentials: "include",
      headers,
      method,
    });
    responseText = await response.text();
    payload = responseText ? safeParseJson(responseText) : {};
  }

  if (!response.ok) {
    const message = payload.error || payload.message || responseText || getRequestCopy().requestFailedGeneric;
    if (isInvalidSessionStatusOrMessage(response.status, message)) {
      sessionInvalidHandler?.({
        code: payload.code || "AUTHENTICATION_REQUIRED",
        message,
      });
    }
    if (response.status === 402 && payload.code === "SUBSCRIPTION_READ_ONLY") {
      notifySubscriptionReadOnly();
    }
    if (
      response.status === 402 &&
      (payload.code === "INSUFFICIENT_CREDITS" || String(message).trim().toLowerCase().includes("insufficient credits"))
    ) {
      notifyInsufficientCredits();
    }
    throw new ApiError(message, response.status, payload.code || "");
  }

  return responseType === "text" ? responseText : payload;
}


function isCsrfFailure(status, payload, responseText) {
  if (status !== 403) {
    return false;
  }

  const message = String(payload?.error || payload?.message || responseText || "").trim().toLowerCase();
  return message.includes("csrf validation failed");
}

function isInvalidSessionStatusOrMessage(status, message) {
  const normalizedMessage = String(message || "").trim().toLowerCase();

  if (status === 401) {
    return true;
  }

  return (
    normalizedMessage.includes("unauthenticated") ||
    normalizedMessage.includes("invalid session") ||
    normalizedMessage.includes("session expired") ||
    normalizedMessage.includes("no active session")
  );
}

function safeParseJson(value) {
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

export function clearRequestCache() {
  csrfToken = null;
  removeStoredValue("shopbook.web.csrf-token");
}

export function setSessionInvalidHandler(handler) {
  sessionInvalidHandler = typeof handler === "function" ? handler : null;
}

export function setSubscriptionReadOnlyHandler(handler) {
  subscriptionReadOnlyHandler = typeof handler === "function" ? handler : null;
}

export function notifySubscriptionReadOnly() {
  subscriptionReadOnlyHandler?.();
}

export function setInsufficientCreditsHandler(handler) {
  insufficientCreditsHandler = typeof handler === "function" ? handler : null;
}

export function notifyInsufficientCredits(details = {}) {
  insufficientCreditsHandler?.(details);
}
